import { memo, type ReactNode } from 'react';
import { Button, Flex, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import { PLAN_KPI_SELECTED_BG, PLAN_TABLE_TOOLBAR_HEIGHT, PLAN_TEXT } from '../planPageRevampUi';
import DownloadIcon from '../icons/DownloadIcon';
import InfoIcon from '../icons/InfoIcon';

import PlanTimeFilterSegment, { PlanTimeFilterValue } from './PlanTimeFilterSegment';

interface Properties {
  hint?: string;
  timeAggregation: string;
  tableFilter: PlanTimeFilterValue;
  onTimeFilterChange: (value: PlanTimeFilterValue) => void;
  onDownload: () => void;
  isExporting?: boolean;
  leadingActions?: ReactNode;
}

const PlanDataTableToolbar = ({
  hint = 'Expand the table to drill into the values for each of your items',
  timeAggregation,
  tableFilter,
  onTimeFilterChange,
  onDownload,
  isExporting = false,
  leadingActions,
}: Properties) => {
  const themeColors = useSelector(getPlanPageThemeColors);

  return (
    <Flex
      h={PLAN_TABLE_TOOLBAR_HEIGHT}
      alignItems='stretch'
      justifyContent='space-between'
      flexShrink={0}
      overflow='hidden'
      borderBottom='1px solid'
      borderColor={themeColors.border}
      bg={PLAN_KPI_SELECTED_BG}
      w='100%'
      maxW='100%'
    >
      <Flex alignItems='center' gap={2} pl={3} minW={0} flex='1'>
        <InfoIcon width={12} height={12} color={themeColors.textSecondary} />
        <Text {...PLAN_TEXT.labelSm} color={themeColors.textSecondary} noOfLines={1}>
          {hint}
        </Text>
      </Flex>

      <Flex alignItems='stretch' gap={1} flexShrink={0} h='100%'>
        {leadingActions}

        {timeAggregation !== 'Y' && (
          <Flex alignItems='center' gap={3} px={4} h='100%'>
            <PlanTimeFilterSegment
              variant='tableTime'
              value={tableFilter}
              onChange={onTimeFilterChange}
            />
          </Flex>
        )}

        <Button
          h='100%'
          minH={PLAN_TABLE_TOOLBAR_HEIGHT}
          px={3}
          gap={2}
          borderRadius={0}
          {...PLAN_TEXT.bodyMedium}
          color={themeColors.white}
          bg={themeColors.primary}
          leftIcon={<DownloadIcon width={16} height={16} color={themeColors.white} />}
          _hover={{ bg: themeColors.primaryHover }}
          _active={{ bg: themeColors.primaryPressed }}
          flexShrink={0}
          isLoading={isExporting}
          loadingText='Downloading'
          onClick={onDownload}
          width='fit-content'
        >
          Download
        </Button>
      </Flex>
    </Flex>
  );
};

export default memo(PlanDataTableToolbar);
